import ResizableDrawer from "@components/ResizableDrawer/ResizableDrawer"
import { EdgeErrors } from "@features/builder/ErrorsDrawer/EdgeErrors"
import { errorsDrawerOpenAtom } from "@features/builder/ErrorsDrawer/errorsDrawerOpenAtom"
import type { EdgeData } from "@features/builder/graph/types"
import { NodesErrors } from "@features/builder/ErrorsDrawer/NodesErrors"
import { SamplingErrors } from "@features/builder/ErrorsDrawer/SamplingErrors"
import { presamplingValidationAtom } from "@features/builder/graph/nodes/validation/presamplingValidationAtom"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
  useTheme,
} from "@mui/material"
import { type Edge, Panel, useEdges } from "@xyflow/react"
import { useAtom, useAtomValue } from "jotai"
import type { ReactNode } from "react"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { shortcuts } from "@features/keymap/shortcuts"
import { useKeymap } from "@features/keymap/useKeymap"

type ErrorsOwnerAccordionProps = {
  title: string
  children: ReactNode
}

const ErrorsOwnerAccordion = ({ children, title }: ErrorsOwnerAccordionProps) => (
  <Accordion defaultExpanded disableGutters square variant="section">
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
)

export const ErrorsDrawer = () => {
  const [isOpen, setIsOpen] = useAtom(errorsDrawerOpenAtom)
  const theme = useTheme()
  const samplingValidation = useAtomValue(presamplingValidationAtom)
  const edges = useEdges<Edge<EdgeData>>()
  const { hasErrors: hasNodeErrors } = useNodesValidation()
  const keymap = useKeymap()
  const hasSamplingErrors = Boolean(
    samplingValidation?.message?.length || samplingValidation?.warnings?.length,
  )
  const hasEdgeErrors = edges.some((edge) => edge.label)
  const hasErrors = hasSamplingErrors || hasEdgeErrors || hasNodeErrors

  if (!hasErrors) return null

  const handleToggle = () => setIsOpen((open) => !open)

  return (
    <>
      <Panel position="bottom-left" style={{ zIndex: theme.zIndex.modal + 1 }}>
        <Button
          color="error"
          data-testid="errors-drawer-toggle"
          variant="contained"
          onClick={handleToggle}
          title={keymap.getKeymaps().byId.get("toggle-errors-drawer")?.desc}
        >
          {isOpen ? "Hide Errors" : "Show Errors"}
        </Button>
        <KeyBinding bind={shortcuts["toggle-errors-drawer"]} onKeyDown={handleToggle} />
      </Panel>

      <ResizableDrawer
        anchor="left"
        defaultWidth={500}
        disableEnforceFocus
        hideBackdrop
        keepMounted
        localStoragePrefix="builder-errors-drawer"
        navigationBarTitle={
          <Typography sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Errors Drawer</Typography>
        }
        navigationBarSx={{
          backgroundColor: "rgb(214, 0, 42)",
          color: "white",
          filter: "drop-shadow(0 0 1.06rem #aaa)",
          "& .MuiIconButton-root": { color: "inherit" },
        }}
        ModalProps={{ disablePortal: true }}
        onClose={() => setIsOpen(false)}
        PaperProps={{ sx: { top: "140px", pb: "80px" } }}
        resizeControlPosition="end"
        open={isOpen}
        SlideProps={{ appear: false }}
      >
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            p: 2,
          }}
        >
          {hasSamplingErrors && (
            <ErrorsOwnerAccordion title="Sampling Errors">
              <SamplingErrors />
            </ErrorsOwnerAccordion>
          )}
          {hasEdgeErrors && (
            <ErrorsOwnerAccordion title="Edge Errors">
              <EdgeErrors />
            </ErrorsOwnerAccordion>
          )}
          {hasNodeErrors && (
            <ErrorsOwnerAccordion title="Node Errors">
              <NodesErrors />
            </ErrorsOwnerAccordion>
          )}
        </Box>
      </ResizableDrawer>
    </>
  )
}
